import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2, ArrowRight, MessageCircle, Mail, CheckCircle, ArrowLeft, Lock, Eye, EyeOff, UserPlus, Ban, Info, Upload, Star
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useClientSession, ClientSession } from "@/hooks/useClientSession";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import TermosDialog, { hashTermo } from "@/components/TermosDialog";

import { isValidPhone, normalizePhone } from "@/lib/phone-utils";

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getFunctionErrorMessage = async (err: unknown, fallback: string): Promise<string> => {
  // Handle FunctionsHttpError (has context as Response)
  if (err && typeof err === "object" && "context" in err) {
    try {
      const res = (err as { context: Response }).context;
      if (res && typeof res.json === "function") {
        const body = await res.json();
        if (typeof body?.error === "string") return body.error;
      }
    } catch { /* ignore */ }
  }
  if (err instanceof Error) {
    const jsonStart = err.message.indexOf("{");
    if (jsonStart !== -1) {
      try {
        const parsed = JSON.parse(err.message.slice(jsonStart));
        if (typeof parsed?.error === "string") return parsed.error;
      } catch { /* ignore */ }
    }
    return err.message || fallback;
  }
  return fallback;
};

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoginSuccess?: () => void;
}

type Step = "identify" | "verify" | "register";

const LOGIN_PREFS_KEY = "rpgcoin_login_prefs";

interface LoginPrefs {
  canal?: "whatsapp" | "email";
  mode?: "code" | "password";
}

const readLoginPrefs = (): LoginPrefs => {
  try {
    const raw = localStorage.getItem(LOGIN_PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LoginPrefs & { contato?: string };
    // Strip any legacy 'contato' field that may have been persisted before
    if (parsed && "contato" in parsed) {
      delete (parsed as any).contato;
      try { localStorage.setItem(LOGIN_PREFS_KEY, JSON.stringify(parsed)); } catch { /* ignore */ }
    }
    return parsed || {};
  } catch {
    return {};
  }
};

const writeLoginPrefs = (prefs: LoginPrefs) => {
  try {
    const current = readLoginPrefs();
    const merged: LoginPrefs = { ...current, ...prefs };
    // Never persist sensitive contact info (email/phone) in localStorage
    delete (merged as any).contato;
    localStorage.setItem(LOGIN_PREFS_KEY, JSON.stringify(merged));
  } catch { /* ignore */ }
};

const LoginDialog = ({ open, onOpenChange, onLoginSuccess }: LoginDialogProps) => {
  const { login } = useClientSession();
  const { data: config } = useConfiguracoes();
  const initialPrefs = readLoginPrefs();
  const [step, setStep] = useState<Step>("identify");
  const [contato, setContato] = useState("");
  const [canal, setCanal] = useState<"whatsapp" | "email">(initialPrefs.canal || "whatsapp");
  const [tokenInput, setTokenInput] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showBloqueado, setShowBloqueado] = useState(false);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setResendCooldown(60);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  // Password login
  const [showPasswordLogin, setShowPasswordLogin] = useState(initialPrefs.mode === "password");
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLogging, setPasswordLogging] = useState(false);

  // Registration step
  const [regSessionToken, setRegSessionToken] = useState("");
  const [regSessionContato, setRegSessionContato] = useState("");
  const [regComplementaryContact, setRegComplementaryContact] = useState("");
  const [regNome, setRegNome] = useState("");
  const [regApelido, setRegApelido] = useState("");
  const [regChavePix, setRegChavePix] = useState("");
  const [regAvatarUrl, setRegAvatarUrl] = useState<string | null>(null);
  const [regUploadingAvatar, setRegUploadingAvatar] = useState(false);
  const [regPersonagem, setRegPersonagem] = useState("");
  const [regSenha, setRegSenha] = useState("");
  const [regSenhaConfirm, setRegSenhaConfirm] = useState("");
  const [regShowSenha, setRegShowSenha] = useState(false);
  const [regSaving, setRegSaving] = useState(false);
  const [regPreferencias, setRegPreferencias] = useState<string[]>(["whatsapp"]);
  const [regRevendedor, setRegRevendedor] = useState(false);

  // Terms state
  const [showTermos, setShowTermos] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState<{
    contato: string; token: string; profile: any;
  } | null>(null);
  const [pendingOverrideCanal, setPendingOverrideCanal] = useState<"whatsapp" | "email" | "senha" | null>(null);

  const resetState = () => {
    const prefs = readLoginPrefs();
    setStep("identify");
    setContato("");
    setCanal(prefs.canal || "whatsapp");
    setShowPasswordLogin(prefs.mode === "password");
    setTokenInput("");
    setPasswordInput("");
    setShowPassword(false);
    setRegSessionToken("");
    setRegSessionContato("");
    setRegComplementaryContact("");
    setRegNome("");
    setRegApelido("");
    setRegChavePix("");
    setRegAvatarUrl(null);
    setRegUploadingAvatar(false);
    setRegPersonagem("");
    setRegSenha("");
    setRegSenhaConfirm("");
    setRegShowSenha(false);
    setRegPreferencias(["whatsapp"]);
    setRegRevendedor(false);
    setShowTermos(false);
    setPendingLoginData(null);
    setPendingOverrideCanal(null);
    setShowTermosNewUser(false);
    setPendingSendContato("");
    setShowBloqueado(false);
    setResendCooldown(0);
    if (cooldownRef.current) { clearInterval(cooldownRef.current); cooldownRef.current = null; }
  };

  // Restore preferences whenever dialog reopens
  useEffect(() => {
    if (!open) return;
    const prefs = readLoginPrefs();
    setCanal(prefs.canal || "whatsapp");
    setShowPasswordLogin(prefs.mode === "password");
    // Note: contato is not pre-filled from storage to avoid leaking PII on shared devices
  }, [open]);

  // Persist canal selection
  useEffect(() => {
    writeLoginPrefs({ canal });
  }, [canal]);

  // Persist password/code mode selection
  useEffect(() => {
    writeLoginPrefs({ mode: showPasswordLogin ? "password" : "code" });
  }, [showPasswordLogin]);

  const handleOpenChange = (open: boolean) => {
    if (!open) resetState();
    onOpenChange(open);
  };

  const [pendingSendContato, setPendingSendContato] = useState("");
  const [showTermosNewUser, setShowTermosNewUser] = useState(false);

  const doSendToken = async (contatoLimpo: string) => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-verification", {
        body: { contato: contatoLimpo, canal },
      });

      // Parse edge function error response to extract friendly message
      if (error) {
        let friendlyMsg = "Erro ao enviar código";
        try {
          if ("context" in (error as any)) {
            const res = (error as any).context as Response;
            if (res && typeof res.json === "function") {
              const body = await res.json();
              if (typeof body?.error === "string") friendlyMsg = body.error;
            }
          } else if (error instanceof Error && error.message) {
            const jsonStart = error.message.indexOf("{");
            if (jsonStart !== -1) {
              const parsed = JSON.parse(error.message.slice(jsonStart));
              if (typeof parsed?.error === "string") friendlyMsg = parsed.error;
            }
          }
        } catch { /* ignore parse errors */ }
        toast.error(friendlyMsg);
        setSending(false);
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        setSending(false);
        return;
      }

      setContato(contatoLimpo);
      toast.success("Código enviado!");
      setStep("verify");
      startCooldown();
    } catch (err: unknown) {
      toast.error(await getFunctionErrorMessage(err, "Erro ao enviar código"));
    }
    setSending(false);
  };

  const handleSendToken = async () => {
    if (resendCooldown > 0 || sending) return;

    let contatoLimpo = contato.trim();
    if (!contatoLimpo) { toast.error("Informe seu contato"); return; }
    if (canal === "whatsapp") {
      if (!isValidPhone(contatoLimpo)) {
        toast.error("Digite um número de telefone válido com DDD"); return;
      }
      contatoLimpo = normalizePhone(contatoLimpo);
    } else {
      if (!isValidEmail(contatoLimpo)) {
        toast.error("Digite um e-mail válido"); return;
      }
    }

    // Terms are checked post-login via checkTermsAndFinish

    await doSendToken(contatoLimpo);
  };

  const sendCodeButtonLabel = resendCooldown > 0
    ? `Enviar código em ${resendCooldown}s`
    : "Enviar código";

  const checkTermsAndFinish = async (sessionContato: string, sessionToken: string, profile: any, termosAceitos?: string[], overrideCanal?: "whatsapp" | "email" | "senha") => {
    const termoUso = config?.termo_uso?.trim();
    if (termoUso) {
      const currentHash = await hashTermo(termoUso);
      const accepted = termosAceitos || [];
      if (!accepted.includes(currentHash)) {
        // Need to accept terms before proceeding
        setPendingLoginData({ contato: sessionContato, token: sessionToken, profile });
        setPendingOverrideCanal(overrideCanal || null);
        setShowTermos(true);
        return;
      }
    }
    doFinishLogin(sessionContato, sessionToken, profile, overrideCanal);
  };

  const doFinishLogin = (sessionContato: string, sessionToken: string, profile: any, overrideCanal?: "whatsapp" | "email" | "senha") => {
    // Persist only non-sensitive UI prefs (mode/canal). Contact is intentionally NOT stored.
    writeLoginPrefs({
      mode: showPasswordLogin ? "password" : "code",
      canal,
    });
    login({
      contato: sessionContato,
      token: sessionToken,
      canal: overrideCanal || (canal as "whatsapp" | "email" | "senha"),
      apelido: profile?.apelido || null,
      avatar_url: profile?.avatar_url || null,
      nome: profile?.nome || null,
      perfil_id: profile?.id || null,
    });
    toast.success("Acesso concedido!");
    resetState();
    onOpenChange(false);
    onLoginSuccess?.();
  };

  const handleVerify = async () => {
    if (tokenInput.length !== 5) { toast.error("Digite o código de 5 dígitos"); return; }
    setVerifying(true);
    try {
      const { data: verifyData, error: verifyErr } = await supabase.functions.invoke("verify-token", {
        body: { contato, token: tokenInput },
      });
      if (verifyErr) throw verifyErr;
      if (!verifyData?.valid) {
        if (verifyData?.error === "bloqueado") {
          setShowBloqueado(true);
          setVerifying(false);
          return;
        }
        toast.error(verifyData?.error || "Código inválido");
        setVerifying(false);
        return;
      }

      const sessionToken = verifyData?.session_token || tokenInput;
      const sessionContato = verifyData?.contato || contato;

      const { data: profileData } = await supabase.functions.invoke("client-profile", {
        body: { contato: sessionContato, token: sessionToken },
      });

      const profile = profileData?.profile;
      const isNewUser = !profile || !profile.apelido;

      if (isNewUser) {
        // Store session info for registration step
        setRegSessionToken(sessionToken);
        setRegSessionContato(sessionContato);
        // Pre-fill with existing data if any
        if (profile?.nome) setRegNome(profile.nome);
        if (profile?.apelido) setRegApelido(profile.apelido);
        if (profile?.chave_pix) setRegChavePix(profile.chave_pix);
        if (profile?.avatar_url) setRegAvatarUrl(profile.avatar_url);
        if (profile?.personagem_principal) setRegPersonagem(profile.personagem_principal);
        if (profile?.revendedor) setRegRevendedor(true);
        // Pre-fill complementary contact if exists
        if (canal === "whatsapp" && profile?.email) setRegComplementaryContact(profile.email);
        if (canal === "email" && profile?.telefone) setRegComplementaryContact(profile.telefone);
        setStep("register");
      } else {
        checkTermsAndFinish(sessionContato, sessionToken, profile, profileData?.termos_aceitos);
      }
    } catch (err: any) {
      toast.error(err.message || "Erro ao verificar");
    }
    setVerifying(false);
  };

  const handleRegisterAvatarUpload = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 2MB");
      return;
    }
    setRegUploadingAvatar(true);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `avatars/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("comprovantes").upload(path, file);
      if (error) {
        toast.error("Erro ao enviar imagem");
        return;
      }
      const { data: urlData } = supabase.storage.from("comprovantes").getPublicUrl(path);
      setRegAvatarUrl(urlData.publicUrl);
      toast.success("Imagem enviada!");
    } finally {
      setRegUploadingAvatar(false);
    }
  };

  const handleRegisterSubmit = async () => {
    const complementaryValue = regComplementaryContact.trim();
    if (!complementaryValue) {
      toast.error(canal === "whatsapp" ? "Informe seu e-mail" : "Informe seu WhatsApp");
      return;
    }
    // Validate complementary contact
    if (canal === "whatsapp") {
      if (!isValidEmail(complementaryValue)) {
        toast.error("Digite um e-mail válido");
        return;
      }
    } else {
      if (!isValidPhone(complementaryValue)) {
        toast.error("Digite um número de telefone válido com DDD");
        return;
      }
    }
    if (!regApelido.trim()) { toast.error("Informe seu apelido"); return; }
    if (regPreferencias.length === 0) { toast.error("Selecione ao menos uma forma de contato"); return; }
    if (!regSenha || regSenha.length < 6) { toast.error("A senha deve ter pelo menos 6 caracteres"); return; }
    if (regSenha !== regSenhaConfirm) { toast.error("As senhas não conferem"); return; }

    setRegSaving(true);
    try {
      // If user logged via email, validate that the provided WhatsApp number actually has WhatsApp
      if (canal === "email") {
        const normalizedPhone = normalizePhone(complementaryValue);
        const { data: waData, error: waErr } = await supabase.functions.invoke("check-whatsapp-public", {
          body: { phone: normalizedPhone, contato: regSessionContato, token: regSessionToken },
        });
        if (waErr) {
          toast.error("Não foi possível validar o WhatsApp. Tente novamente.");
          setRegSaving(false);
          return;
        }
        if (waData?.has_whatsapp === false) {
          toast.error("Este número não possui WhatsApp ativo. Informe um número válido.");
          setRegSaving(false);
          return;
        }
      }

      const profileUpdate: Record<string, any> = {
        nome: regNome.trim(),
        apelido: regApelido.trim(),
        chave_pix: regChavePix.trim(),
        preferencias_contato: regPreferencias,
        revendedor: regRevendedor,
      };
      if (regAvatarUrl) {
        profileUpdate.avatar_url = regAvatarUrl;
      }
      if (regPersonagem.trim()) {
        profileUpdate.personagem_principal = regPersonagem.trim();
      }
      if (canal === "whatsapp") {
        profileUpdate.email = complementaryValue.toLowerCase();
      } else {
        profileUpdate.telefone = normalizePhone(complementaryValue).slice(2);
      }

      const { data: saveData, error: saveErr } = await supabase.functions.invoke("client-profile", {
        body: {
          contato: regSessionContato,
          token: regSessionToken,
          profile: profileUpdate,
        },
      });
      if (saveErr) throw saveErr;
      if (saveData?.error) throw new Error(saveData.error);

      // Set password
      const { data: pwData, error: pwErr } = await supabase.functions.invoke("client-set-password", {
        body: {
          contato: regSessionContato,
          token: regSessionToken,
          senha: regSenha,
        },
      });
      if (pwErr) throw pwErr;
      if (pwData?.error) throw new Error(pwData.error);

      const savedProfile = saveData?.profile;
      // For new users, terms were accepted during registration (shown before code send)
      // so we can login directly
      const canalLogin = "senha" as const;
      login({
        contato: regSessionContato,
        token: regSessionToken,
        canal: canalLogin,
        apelido: savedProfile?.apelido || regApelido.trim(),
        avatar_url: savedProfile?.avatar_url || null,
        nome: savedProfile?.nome || regNome.trim(),
        perfil_id: savedProfile?.id || null,
      });
      toast.success("Cadastro realizado com sucesso!");
      resetState();
      onOpenChange(false);
      onLoginSuccess?.();
    } catch (err: unknown) {
      toast.error(await getFunctionErrorMessage(err, "Erro ao salvar cadastro"));
    }
    setRegSaving(false);
  };

  const handlePasswordLogin = async () => {
    let contatoLimpo = contato.trim();
    if (!contatoLimpo) { toast.error("Informe seu contato"); return; }
    const isPhone = !contatoLimpo.includes("@");
    if (isPhone) {
      if (!isValidPhone(contatoLimpo)) {
        toast.error("Digite um número de telefone válido com DDD"); return;
      }
      contatoLimpo = normalizePhone(contatoLimpo);
    } else {
      if (!isValidEmail(contatoLimpo)) {
        toast.error("Digite um e-mail válido"); return;
      }
    }
    if (!passwordInput) { toast.error("Informe sua senha"); return; }
    setPasswordLogging(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-login", {
        body: { contato: contatoLimpo, senha: passwordInput },
      });
      if (error) throw error;
      if (data?.error) {
        if (data.error === "bloqueado") {
          setShowBloqueado(true);
          setPasswordLogging(false);
          return;
        }
        throw new Error(data.error);
      }

      // Check terms before finishing login
      checkTermsAndFinish(
        data.contato || contatoLimpo,
        data.token,
        data.profile,
        data.termos_aceitos,
        "senha"
      );
    } catch (err: unknown) {
      toast.error(await getFunctionErrorMessage(err, "Credenciais inválidas"));
    }
    setPasswordLogging(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-md" onPointerDownOutside={(e) => step === "register" && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary gold-text-glow text-center">
            {step === "register" ? "COMPLETE SEU CADASTRO" : "ACESSO"}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground text-center">
            {step === "register"
              ? "Preencha as informações abaixo para continuar."
              : "Informe seu contato para receber o código de acesso."}
          </DialogDescription>
        </DialogHeader>

        {step === "identify" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {!showPasswordLogin && (
              <>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCanal("whatsapp")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                      canal === "whatsapp" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="font-pixel text-[8px]">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => setCanal("email")}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                      canal === "email" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="font-pixel text-[8px]">E-mail</span>
                  </button>
                </div>
                <div>
                  <input
                    type={canal === "email" ? "email" : "tel"}
                    autoComplete={canal === "email" ? "email" : "tel"}
                    className={`tibia-input rounded-sm ${contato.trim() && (canal === "whatsapp" ? !isValidPhone(contato) : !isValidEmail(contato)) ? "border-destructive" : ""}`}
                    placeholder={canal === "whatsapp" ? "11999999999 ou +1234567890" : "seu@email.com"}
                    value={contato}
                    onChange={(e) => setContato(e.target.value)}
                  />
                   {contato.trim() && (canal === "whatsapp" ? !isValidPhone(contato) : !isValidEmail(contato)) && (
                    <p className="text-[10px] text-destructive mt-1">
                      {canal === "whatsapp" ? "Número inválido. Use DDD + número (ex: 11999999999 ou +1234567890)" : "E-mail inválido (ex: nome@email.com)"}
                    </p>
                  )}
                </div>
                <motion.button
                  whileHover={{ scale: resendCooldown > 0 || sending ? 1 : 1.02 }}
                  whileTap={{ scale: resendCooldown > 0 || sending ? 1 : 0.98 }}
                  onClick={handleSendToken}
                  disabled={!contato.trim() || sending || resendCooldown > 0 || (canal === "whatsapp" ? !isValidPhone(contato) : !isValidEmail(contato))}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <span className="font-pixel text-[8px]">{sendCodeButtonLabel}</span>
                      {resendCooldown === 0 && <ArrowRight className="w-3 h-3" />}
                    </>
                  )}
                </motion.button>
              </>
            )}

            {showPasswordLogin && (
              <>
                <input
                  type="text"
                  autoComplete="username"
                  className="tibia-input rounded-sm"
                  placeholder="WhatsApp ou E-mail"
                  value={contato}
                  onChange={(e) => setContato(e.target.value)}
                />
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="tibia-input rounded-sm pr-10"
                    placeholder="Sua senha"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePasswordLogin}
                  disabled={!contato || !passwordInput || passwordLogging}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {passwordLogging ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Lock className="w-3 h-3" />
                      <span className="font-pixel text-[8px]">Entrar</span>
                    </>
                  )}
                </motion.button>
              </>
            )}

            <button
              onClick={() => {
                setShowPasswordLogin(!showPasswordLogin);
                setPasswordInput("");
              }}
              className="block w-full text-center text-xs text-primary/80 hover:text-primary transition-colors"
            >
              {showPasswordLogin ? "← Acessar com código" : "🔑 Acessar com senha"}
            </button>
          </motion.div>
        )}

        {step === "verify" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="text-center">
              <CheckCircle className="w-10 h-10 text-primary mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                Código enviado para <span className="text-primary">{contato}</span>
              </p>
            </div>
            <input
              type="text"
              maxLength={5}
              className="tibia-input rounded-sm text-center text-2xl tracking-[0.5em]"
              placeholder="00000"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setStep("identify");
                  setTokenInput("");
                  if (resendCooldown > 0) {
                    toast.info(`Se voltar, aguarde ${resendCooldown}s para solicitar um novo código.`);
                  }
                }}
                className="tibia-tab flex-1 rounded-sm hover:text-primary flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> Voltar
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleVerify}
                disabled={tokenInput.length !== 5 || verifying}
                className="tibia-btn flex-1 rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <span className="font-pixel text-[8px]">Verificar</span>
                )}
              </motion.button>
            </div>
            <button
              onClick={async () => {
                if (resendCooldown > 0 || sending) return;
                await doSendToken(contato);
              }}
              disabled={resendCooldown > 0 || sending}
              className="block w-full text-center text-xs text-primary/80 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? (
                <span className="flex items-center justify-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Enviando...</span>
              ) : resendCooldown > 0 ? (
                `Reenviar código em ${resendCooldown}s`
              ) : (
                "Reenviar código"
              )}
            </button>
          </motion.div>
        )}

        {step === "register" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="text-center mb-2">
              <UserPlus className="w-8 h-8 text-primary mx-auto mb-1" />
              {regSessionContato && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Acessando com <span className="text-primary font-medium break-all">{regSessionContato}</span>
                </p>
              )}
            </div>

            {/* Avatar upload (optional) */}
            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Imagem de perfil</label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative w-12 h-12 rounded-sm border-2 border-dashed border-border/50 group-hover:border-primary/50 flex items-center justify-center transition-all overflow-hidden bg-card">
                  {regAvatarUrl ? (
                    <img src={regAvatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : regUploadingAvatar ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : (
                    <Upload className="w-4 h-4 text-muted-foreground" />
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">
                  {regAvatarUrl ? "Trocar imagem" : "Enviar imagem"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={regUploadingAvatar}
                  onChange={(e) => e.target.files?.[0] && handleRegisterAvatarUpload(e.target.files[0])}
                />
              </label>
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">
                {canal === "whatsapp" ? "E-mail *" : "WhatsApp *"}
              </label>
              <input
                type="text"
                className="tibia-input rounded-sm"
                placeholder={canal === "whatsapp" ? "seu@email.com" : "11999999999"}
                value={regComplementaryContact}
                maxLength={canal === "whatsapp" ? 100 : 15}
                onChange={(e) => setRegComplementaryContact(e.target.value)}
                onPaste={(e) => { const max = canal === "whatsapp" ? 100 : 15; const pasted = e.clipboardData.getData("text"); if ((regComplementaryContact + pasted).length > max) { e.preventDefault(); setRegComplementaryContact((regComplementaryContact + pasted).slice(0, max)); } }}
              />
            </div>

            {/* Contact preferences */}
            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Como deseja receber contato? *</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "whatsapp", label: "WhatsApp", icon: "💬" },
                  { key: "email", label: "E-mail", icon: "✉️" },
                  { key: "plataforma", label: "Plataforma", icon: "💻" },
                ].map((opt) => (
                  <label key={opt.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={regPreferencias.includes(opt.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRegPreferencias((p) => [...p, opt.key]);
                        } else {
                          setRegPreferencias((p) => p.filter((v) => v !== opt.key));
                        }
                      }}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-[9px]">{opt.icon} {opt.label}</span>
                  </label>
                ))}
              </div>
              {regPreferencias.length === 0 && (
                <p className="text-[8px] text-destructive mt-0.5">Selecione ao menos uma opção</p>
              )}
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Apelido *</label>
              <input
                type="text"
                className="tibia-input rounded-sm"
                placeholder="Como quer ser chamado"
                maxLength={50}
                value={regApelido}
                onChange={(e) => setRegApelido(e.target.value)}
                onPaste={(e) => { const pasted = e.clipboardData.getData("text"); if ((regApelido + pasted).length > 50) { e.preventDefault(); setRegApelido((regApelido + pasted).slice(0, 50)); } }}
              />
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground mb-1 flex items-center gap-1">
                <span>Chave PIX</span>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Informação sobre chave PIX">
                        <Info className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[220px] text-[10px]">
                      Utilizado para operações de compra e venda dentro da plataforma
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <input
                type="text"
                className="tibia-input rounded-sm"
                placeholder="CPF, e-mail, telefone ou chave aleatória"
                maxLength={100}
                value={regChavePix}
                onChange={(e) => setRegChavePix(e.target.value)}
              />
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Nome (Titular da chave PIX)</label>
              <input
                type="text"
                className="tibia-input rounded-sm"
                placeholder="Seu nome completo (opcional)"
                maxLength={50}
                value={regNome}
                onChange={(e) => setRegNome(e.target.value)}
                onPaste={(e) => { const pasted = e.clipboardData.getData("text"); if ((regNome + pasted).length > 50) { e.preventDefault(); setRegNome((regNome + pasted).slice(0, 50)); } }}
              />
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Personagem principal</label>
              <input
                type="text"
                className="tibia-input rounded-sm"
                placeholder="Nome do personagem (opcional)"
                maxLength={50}
                value={regPersonagem}
                onChange={(e) => setRegPersonagem(e.target.value)}
              />
            </div>

            <label
              className="relative flex items-center gap-3 p-3 rounded-sm border-2 border-primary/60 cursor-pointer overflow-hidden bg-gradient-to-r from-primary/10 via-amber-500/10 to-primary/10 hover:from-primary/20 hover:via-amber-500/20 hover:to-primary/20 transition-all"
              style={{ boxShadow: "0 0 12px hsl(var(--primary) / 0.45)" }}
            >
              <span className="pointer-events-none absolute inset-0 rounded-sm animate-pulse" style={{ boxShadow: "inset 0 0 18px hsl(var(--primary) / 0.35)" }} />
              <input
                type="checkbox"
                className="relative z-10 w-4 h-4 accent-primary cursor-pointer"
                checked={regRevendedor}
                onChange={(e) => setRegRevendedor(e.target.checked)}
              />
              <div className="relative z-10 flex flex-col">
                <span className="font-pixel text-[9px] text-primary uppercase tracking-wide flex items-center gap-1">
                  <Star className="w-3 h-3 fill-primary" /> Sou revendedor
                </span>
                <span className="text-[10px] text-muted-foreground">Marque se você compra e revende itens na plataforma</span>
              </div>
            </label>




            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Senha *</label>
              <div className="relative">
                <input
                  type={regShowSenha ? "text" : "password"}
                  className="tibia-input rounded-sm pr-10"
                  placeholder="Mínimo 6 caracteres"
                  maxLength={100}
                  value={regSenha}
                  onChange={(e) => setRegSenha(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setRegShowSenha(!regShowSenha)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                >
                  {regShowSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-pixel text-muted-foreground block mb-1">Confirmar senha *</label>
              <input
                type={regShowSenha ? "text" : "password"}
                className="tibia-input rounded-sm"
                placeholder="Repita a senha"
                maxLength={100}
                value={regSenhaConfirm}
                onChange={(e) => setRegSenhaConfirm(e.target.value)}
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRegisterSubmit}
              disabled={regSaving}
              className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {regSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle className="w-3 h-3" />
                  <span className="font-pixel text-[8px]">Salvar e Continuar</span>
                </>
              )}
            </motion.button>
          </motion.div>
        )}

        <div className="text-center pt-2 border-t border-border/30">
          <a
            href="/politica-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="font-pixel text-[7px] text-muted-foreground hover:text-primary transition-colors"
          >
            Política de Privacidade
          </a>
        </div>
      </DialogContent>
    </Dialog>

    {/* Terms dialog for existing users after login */}
    {showTermos && pendingLoginData && config?.termo_uso && (
      <TermosDialog
        open={showTermos}
        onOpenChange={() => {}}
        termoTexto={config.termo_uso}
        perfilId={pendingLoginData.profile?.id || ""}
        contato={pendingLoginData.contato}
        token={pendingLoginData.token}
        onAccepted={() => {
          setShowTermos(false);
          doFinishLogin(pendingLoginData.contato, pendingLoginData.token, pendingLoginData.profile, pendingOverrideCanal || undefined);
        }}
      />
    )}

    {/* Terms dialog for new users before sending code */}
    {showTermosNewUser && config?.termo_uso && (
      <TermosNewUserDialog
        open={showTermosNewUser}
        termoTexto={config.termo_uso}
        onAccepted={async () => {
          setShowTermosNewUser(false);
          await doSendToken(pendingSendContato);
        }}
        onCancel={() => {
          setShowTermosNewUser(false);
          setPendingSendContato("");
        }}
      />
    )}

    {/* Blocked user dialog */}
    <Dialog open={showBloqueado} onOpenChange={setShowBloqueado}>
      <DialogContent className="tibia-border bg-card max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-destructive text-center flex items-center justify-center gap-2">
            <Ban className="w-5 h-5" /> CONTA BLOQUEADA
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground text-center">
            Sua conta foi bloqueada pelo administrador.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            Entre em contato com o administrador para mais informações.
          </p>
          {config?.whatsapp_numero && (
            <a
              href={`https://wa.me/${config.whatsapp_numero}?text=${encodeURIComponent("Olá, minha conta foi bloqueada e gostaria de entender o motivo.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tibia-btn rounded-sm flex items-center justify-center gap-2 w-full"
              style={{ backgroundColor: "#25D366" }}
            >
              <MessageCircle className="w-4 h-4" />
              <span className="font-pixel text-[8px] text-white">Falar no WhatsApp</span>
            </a>
          )}
          <button
            onClick={() => setShowBloqueado(false)}
            className="tibia-tab rounded-sm px-4 py-2 w-full"
          >
            <span className="font-pixel text-[8px]">Fechar</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

/** Lightweight terms dialog for new users (no profile yet, just show & accept) */
const TermosNewUserDialog = ({ open, termoTexto, onAccepted, onCancel }: {
  open: boolean;
  termoTexto: string;
  onAccepted: () => void;
  onCancel: () => void;
}) => {
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      setScrolledToEnd(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="tibia-border bg-card max-w-lg max-h-[90vh] flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary gold-text-glow text-center">
            📜 TERMOS DE USO
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground text-center">
            Para criar sua conta, leia e aceite os termos abaixo.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto tibia-border-inner bg-secondary/20 rounded-sm p-4 max-h-[50vh] text-xs text-foreground whitespace-pre-wrap leading-relaxed"
          onScroll={handleScroll}
        >
          {termoTexto}
        </div>

        {!scrolledToEnd && (
          <p className="text-[10px] text-muted-foreground text-center animate-pulse">
            ↓ Role até o final para aceitar ↓
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="tibia-tab rounded-sm flex-1 py-2 font-pixel text-[8px]"
          >
            Cancelar
          </button>
          <motion.button
            whileHover={scrolledToEnd ? { scale: 1.02 } : {}}
            whileTap={scrolledToEnd ? { scale: 0.98 } : {}}
            onClick={onAccepted}
            disabled={!scrolledToEnd}
            className="tibia-btn flex-1 rounded-sm flex items-center justify-center gap-2 disabled:opacity-40 py-2"
          >
            <CheckCircle className="w-3 h-3" />
            <span className="font-pixel text-[8px]">Aceito</span>
          </motion.button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
